/**
 * ==============================================================================
 * IMPULSE GRID SWIPER ENHANCEMENT
 * ==============================================================================
 * Adds Swiper.js touch/swipe functionality to Impulse theme's product grid.
 * Converts the hover secondary image into a swipeable slider.
 * Also adds infinite scroll (load more on scroll) functionality.
 * ==============================================================================
 */

(function() {
  'use strict';

  console.log('Impulse Grid Swiper: Script loaded');

  // ===========================================================================
  // PRODUCT IMAGE SWIPER
  // ===========================================================================

  /**
   * Copy all image attributes including lazy loading data attributes
   */
  function copyImageAttributes(source, target) {
    // Copy all attributes from source to target
    Array.from(source.attributes).forEach(function(attr) {
      target.setAttribute(attr.name, attr.value);
    });

    // Ensure src is set from data-src if using lazy loading
    if (source.dataset.src && !target.src) {
      target.src = source.dataset.src;
    }
    if (source.dataset.srcset && !target.srcset) {
      target.srcset = source.dataset.srcset;
    }
  }

  /**
   * Initialize Swiper on all product grid items
   */
  function initGridSwipers() {
    // Wait for Swiper to be available
    if (typeof Swiper === 'undefined') {
      setTimeout(initGridSwipers, 100);
      return;
    }

    // Find all product grid items that have a secondary image
    var products = document.querySelectorAll('.grid-product:not([data-swiper-init])');

    products.forEach(function(product) {
      var imageMask = product.querySelector('.grid-product__image-mask');
      var mainImageContainer = product.querySelector('.grid__image-ratio');
      var secondaryImage = product.querySelector('.grid-product__secondary-image');

      // Skip if no secondary image or already initialized
      if (!mainImageContainer || !secondaryImage) return;

      // Mark as initialized
      product.setAttribute('data-swiper-init', 'true');

      // Get images
      var mainImg = mainImageContainer.querySelector('img');
      var secondImg = secondaryImage.querySelector('img');

      if (!mainImg || !secondImg) return;

      // Get actual image sources (handle lazy loading)
      var mainSrc = mainImg.currentSrc || mainImg.src || mainImg.dataset.src;
      var secondSrc = secondImg.currentSrc || secondImg.src || secondImg.dataset.src;

      // Skip if no valid image sources
      if (!mainSrc || !secondSrc) return;

      // Create Swiper structure
      var swiperContainer = document.createElement('div');
      swiperContainer.className = 'grid-product-swiper swiper';

      var swiperWrapper = document.createElement('div');
      swiperWrapper.className = 'swiper-wrapper';

      // Create slide 1 with main image
      var slide1 = document.createElement('div');
      slide1.className = 'swiper-slide';
      var img1 = document.createElement('img');
      img1.src = mainSrc;
      img1.alt = mainImg.alt || '';
      img1.className = 'grid-product-swiper__image';
      if (mainImg.srcset) img1.srcset = mainImg.srcset;
      if (mainImg.sizes) img1.sizes = mainImg.sizes;
      slide1.appendChild(img1);

      // Create slide 2 with secondary image
      var slide2 = document.createElement('div');
      slide2.className = 'swiper-slide';
      var img2 = document.createElement('img');
      img2.src = secondSrc;
      img2.alt = secondImg.alt || '';
      img2.className = 'grid-product-swiper__image';
      if (secondImg.srcset) img2.srcset = secondImg.srcset;
      if (secondImg.sizes) img2.sizes = secondImg.sizes;
      slide2.appendChild(img2);

      swiperWrapper.appendChild(slide1);
      swiperWrapper.appendChild(slide2);

      // Pagination dots - append to image-mask (the visible container) not swiper
      // This ensures dots are positioned relative to the clipped visible area
      var pagination = document.createElement('div');
      pagination.className = 'grid-product-swiper__pagination swiper-pagination';

      swiperContainer.appendChild(swiperWrapper);

      // Replace the original image container content
      mainImageContainer.innerHTML = '';
      mainImageContainer.appendChild(swiperContainer);

      // Append pagination to image-mask (parent of image-ratio) for proper positioning
      imageMask.appendChild(pagination);

      // Hide the secondary image (we've moved it into the slider)
      secondaryImage.style.display = 'none';

      // Track last swipe timestamp for click prevention
      var lastSwipeTime = 0;

      // Initialize Swiper with autoplay module loaded but stopped
      var swiper = new Swiper(swiperContainer, {
        slidesPerView: 1,
        spaceBetween: 0,
        loop: true,
        touchRatio: 1,
        threshold: 10,
        touchAngle: 45,
        grabCursor: true,
        // Initialize autoplay module but stop it immediately
        autoplay: {
          delay: 6000,
          disableOnInteraction: false
        },
        pagination: {
          el: pagination,
          clickable: true
        },
        on: {
          init: function(s) {
            // Stop autoplay immediately after init
            s.autoplay.stop();
          },
          slideChange: function() {
            // Record timestamp when slide actually changes (indicates a real swipe)
            lastSwipeTime = Date.now();
          }
        }
      });

      // Start autoplay on hover, stop on mouse leave
      product.addEventListener('mouseenter', function() {
        swiper.autoplay.start();
      });

      product.addEventListener('mouseleave', function() {
        swiper.autoplay.stop();
        // Reset to first slide when mouse leaves
        swiper.slideTo(0);
      });

      // Handle clicks on swiper - navigate to product if not swiping
      // The swiper sits on top of the product link, so we need to handle navigation manually
      var productLink = product.querySelector('.grid-product__link');
      if (productLink) {
        var productUrl = productLink.getAttribute('href');

        // Prevent click on product link after swipe
        productLink.addEventListener('click', function(e) {
          // Block click for 300ms after a slide change
          if (Date.now() - lastSwipeTime < 300) {
            e.preventDefault();
          }
        });

        // Add click handler to swiper slides to navigate to product
        swiperContainer.addEventListener('click', function(e) {
          // Don't navigate if we just finished swiping (within 300ms)
          if (Date.now() - lastSwipeTime < 300) {
            return;
          }
          // Don't navigate if clicking on pagination dots
          if (e.target.closest('.swiper-pagination')) {
            return;
          }
          // Navigate to product page
          if (productUrl) {
            window.location.href = productUrl;
          }
        });
      }
    });
  }

  // ===========================================================================
  // LOAD MORE BUTTON
  // ===========================================================================

  var loadMoreState = {
    loading: false,
    currentPage: 1,
    hasMorePages: true
  };

  /**
   * Initialize load more button for collection pages
   */
  function initLoadMore() {
    // Only run on collection pages - try multiple selectors
    var collectionProducts = document.querySelector('.collection-products, .collection-grid, .collection, [data-section-type="collection"]');
    if (!collectionProducts) {
      console.log('Load More: No collection container found');
      return;
    }

    // Find and hide existing pagination - try multiple selectors
    var pagination = document.querySelector('.pagination, .pagination-wrapper, .collection-pagination');
    if (pagination) {
      pagination.style.display = 'none';
      console.log('Load More: Pagination hidden');
    }

    // Check if there are more pages - try multiple selectors
    var nextPageLink = document.querySelector('.pagination .next, .pagination__next, [rel="next"], .pagination a[href*="page="]');
    if (!nextPageLink) {
      loadMoreState.hasMorePages = false;
      console.log('Load More: No next page link found - only one page of products');
      return;
    }

    // Find the product grid - try multiple selectors
    var productGrid = document.querySelector('.collection-products .grid, .grid--uniform, .collection-grid .grid');
    if (!productGrid) {
      // Fallback: find the parent of grid-product items
      var firstProduct = document.querySelector('.grid-product');
      if (firstProduct) {
        productGrid = firstProduct.parentElement;
      }
    }
    if (!productGrid) {
      console.log('Load More: No product grid found');
      return;
    }
    console.log('Load More: Initialized successfully, grid:', productGrid.className);

    // Create load more container
    var loadMoreContainer = document.createElement('div');
    loadMoreContainer.className = 'load-more-container';

    // Create load more button
    var loadMoreBtn = document.createElement('button');
    loadMoreBtn.className = 'load-more-btn btn';
    loadMoreBtn.innerHTML = '<span>Meer producten laden</span>';
    loadMoreBtn.type = 'button';

    // Create loading spinner (hidden by default)
    var loadingSpinner = document.createElement('div');
    loadingSpinner.className = 'load-more-spinner';
    loadingSpinner.innerHTML = '<div class="spinner"></div>';
    loadingSpinner.style.display = 'none';

    loadMoreContainer.appendChild(loadMoreBtn);
    loadMoreContainer.appendChild(loadingSpinner);

    // Insert after product grid
    productGrid.parentNode.insertBefore(loadMoreContainer, productGrid.nextSibling);

    // Click handler
    loadMoreBtn.addEventListener('click', function() {
      if (loadMoreState.loading || !loadMoreState.hasMorePages) return;
      loadMoreProducts(loadMoreBtn, loadingSpinner, productGrid, loadMoreContainer);
    });
  }

  /**
   * Load more products via AJAX
   */
  function loadMoreProducts(button, spinner, productGrid, container) {
    loadMoreState.loading = true;
    button.style.display = 'none';
    spinner.style.display = 'flex';

    // Get next page URL
    loadMoreState.currentPage++;
    var currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('page', loadMoreState.currentPage);

    fetch(currentUrl.toString())
      .then(function(response) {
        return response.text();
      })
      .then(function(html) {
        // Parse the response
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');

        // Get new product items
        var newProducts = doc.querySelectorAll('.collection-products .grid__item.grid-product');

        if (newProducts.length === 0) {
          loadMoreState.hasMorePages = false;
          container.innerHTML = '<span class="load-more-done">Alle producten geladen</span>';
          setTimeout(function() {
            container.style.display = 'none';
          }, 2000);
          return;
        }

        // Append new products to grid
        newProducts.forEach(function(product) {
          productGrid.appendChild(product.cloneNode(true));
        });

        // Check if there's a next page
        var nextPageLink = doc.querySelector('.pagination .next, .pagination__next, [rel="next"]');
        if (!nextPageLink) {
          loadMoreState.hasMorePages = false;
          container.innerHTML = '<span class="load-more-done">Alle producten geladen</span>';
          setTimeout(function() {
            container.style.display = 'none';
          }, 2000);
        } else {
          // Show button again
          button.style.display = 'inline-flex';
        }

        // Initialize Swiper on new products
        initGridSwipers();

        // Trigger AOS animations on new elements
        if (typeof AOS !== 'undefined') {
          AOS.refresh();
        }

        // Trigger theme's product init (for quick add, etc.)
        if (typeof theme !== 'undefined' && theme.initQuickAdd) {
          theme.initQuickAdd();
        }

        // Dispatch event for other scripts
        document.dispatchEvent(new CustomEvent('products:loaded'));
      })
      .catch(function(error) {
        console.error('Error loading more products:', error);
        button.style.display = 'inline-flex';
      })
      .finally(function() {
        loadMoreState.loading = false;
        spinner.style.display = 'none';
      });
  }

  // ===========================================================================
  // INITIALIZATION
  // ===========================================================================

  function init() {
    console.log('Impulse Grid Swiper: Init running');
    initGridSwipers();
    initLoadMore();

    // Re-init when new content is added (Shopify sections)
    document.addEventListener('shopify:section:load', function() {
      initGridSwipers();
    });

    // Watch for dynamically added products (AJAX recommendations, recently viewed, etc.)
    var observer = new MutationObserver(function(mutations) {
      var hasNewProducts = mutations.some(function(mutation) {
        return Array.from(mutation.addedNodes).some(function(node) {
          return node.nodeType === 1 && (
            node.classList && node.classList.contains('grid-product') ||
            node.querySelector && node.querySelector('.grid-product')
          );
        });
      });

      if (hasNewProducts) {
        setTimeout(initGridSwipers, 100);
      }
    });

    // Observe multiple containers where products may be dynamically loaded:
    // - Collection pages: .collection-products, .grid--uniform
    // - PDP recommendations: [data-section-type="product-recommendations"]
    // - Recently viewed: [data-section-type="recently-viewed"]
    // - Any grid with products
    var containers = document.querySelectorAll(
      '.collection-products, .grid--uniform, ' +
      '[data-section-type="product-recommendations"], ' +
      '[data-section-type="recently-viewed"], ' +
      '.product-recommendations, .recently-viewed'
    );

    containers.forEach(function(container) {
      observer.observe(container, { childList: true, subtree: true });
    });

    // Also observe body as fallback for any dynamically added sections
    // This catches cases where the section container itself is added dynamically
    var bodyObserver = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1) {
            // Check if added node contains products
            var products = node.querySelectorAll ? node.querySelectorAll('.grid-product:not([data-swiper-init])') : [];
            if (products.length > 0) {
              setTimeout(initGridSwipers, 100);
            }
          }
        });
      });
    });

    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  // Run on DOMContentLoaded or immediately if DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
